/**
 * Generic countdown timer.
 *
 * Features:
 * - Start and stop a countdown
 * - Receive timer updates every second
 * - Be notified when the countdown completes
 *
 
 * Example-
 * const timer = new Timer({
 *   onTick: ({ display }) => {
 *     console.log(display.formatted);
 *   },
 *   onCompleted: () => {
 *     console.log('Countdown completed');
 *   },
 * });
 *
 * timer.start({
 *   expiryTime: '2026-07-21T18:30:00Z',
 * });
 * 
 */

import { TimerCallbacks, TimerConfig, TimerDisplay, TimerState, TimerStatus } from './Timer.types';

export class Timer {
  // Timer interval identifier.
  private intervalId?: ReturnType<typeof setInterval>;

  // Timer expiry timestamp.
  private expiryTime = 0;

  // Timer state.
  private state: TimerState = {
    status: TimerStatus.IDLE,
    remainingTimeMs: 0,
    display: {
      formatted: '00:00',
      minutes: 0,
      seconds: 0,
    },
  };

  // Deafult interval of 1 sec
  private timerIntervalMs = 1000;

  /**
   * Creates a timer instance.
   *
   * @param callbacks Optional lifecycle callbacks.
   */
  constructor(private readonly callbacks: TimerCallbacks) {}

  /**
   * Starts the countdown.
   *
   * If a timer is already running, it is stopped before starting a new one.
   *
   * @param config Timer configuration.
   *
   * Example
   * ts
   * timer.start({
   *   expiryTime: '2026-07-21T18:30:00Z',
   * });
   *
   * Note: This start method initializes the timer and calls the first tick method via setInterval.
   *
   */
  public start(config: TimerConfig): void {
    this.stop();
    this.expiryTime = new Date(config.expiryTime).getTime();
    this.state = { ...this.state, status: TimerStatus.RUNNING };
    this.tick();
    this.intervalId = setInterval(this.tick, this.timerIntervalMs);
  }

  /**
   * Stops the countdown.
   *
   * Note: This method clears the inteval set in start method
   */
  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    this.state = { ...this.state, status: TimerStatus.IDLE };
  }

  /**
   * Returns the latest complete timer state.
   * 
   * Example -  
   * {
    status: TimerStatus.IDLE,
    remainingTimeMs: 0,
    display: {
      formatted: '00:00',
      minutes: 0,
      seconds: 0,
    },
   */
  public getState(): TimerState {
    return this.state;
  }

  /**
   * Updates the timer state on every Tick/interval by compairing current date and expire date.
   * updates values: current timer status, remaining time, display time
   * callback the parent method if present
   * stops the timer if timer value is 0
   * calls expire callback
   */
  private tick = (): void => {
    // Updates the timer state on every Tick/interval by compairing current date and expire date.
    const remainingTimeMs = Math.max(this.expiryTime - Date.now(), 0);

    // updates values: current timer status, remaining time, display time
    this.state = {
      ...this.state,
      status: remainingTimeMs === 0 ? TimerStatus.COMPLETED : TimerStatus.RUNNING,
      remainingTimeMs,
      display: this.getDisplayValue(remainingTimeMs),
    };
    // callback the parent method with state if present to add feature specific logic
    this.callbacks.onTick?.(this.state);

    // stops the timer if timer value is 0
    if (remainingTimeMs <= 0) {
      this.stop();
      this.state = { ...this.state, status: TimerStatus.COMPLETED };

      // calls expire timer callback
      this.callbacks.onCompleted?.();
    }
  };

  /**
   * Converts the remaining time into display values.
   * Converts from miliseconds to )5:30 format
   */

  private getDisplayValue(remainingTimeMs: number): TimerDisplay {
    const totalSeconds = Math.floor(remainingTimeMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return {
      formatted: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      minutes,
      seconds,
    };
  }
}
